import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from "react-router-dom";
import { clearEvent, getEvent } from '../../store/ducks/event/actions';
import { hideFixedFooterContent, hideMobileOptions, showMobileOptions } from 'store/ducks/nav/actions';
import { EventState } from 'store/ducks/event/types';
import PageNotFoundMessage from 'components/pageNotFound/PageNotFoundMessage/PageNotFoundMessage';
import { RenderIf } from 'components/layout';
import { loadRemote } from 'utils/moduleFederation';
import {
	EventSeatsContainer,
	EventSeatsMobileNavBar,
	MapCaption,
	MapCaptionCircle,
	MapCaptionContainer,
	NavBarContent,
	NavEventTitle,
	NavIconContainer,
	NavTitle,
	NavTitleContainer,
	PurchaseModeContent,
	PurchaseModeOption,
	PurchaseModeSection,
	PurchaseModeSubtitle,
	PurchaseModeTitle,
	SeatsMapContainer,
	SeatsMapContentContainer
} from "./styles";
import { ArrowLeft, ChevronRight } from "react-feather";
import Skeleton from "react-loading-skeleton";
import { useEvent } from "./useEvent";
import { isMobile } from "react-device-detect";
import LoadingSpinner from "../../components/event/LoadingSpinner/LoadingSpinner";
import OutsideMapService from "services/OutsideMapService";
import TicketService from "services/TicketService";
import { formatCurrency } from "utils/currencyUtils";
import { AvailableTicket } from "./types";
import Variables from "../../styles/Variables";

interface StateProps {
	event: EventState,
}

const EventSeatsMapPage = () => {
	const { url, eventDate } = useParams<{ url: string, eventDate: string }>();
	const select = (state: StateProps) => state.event;
	const dispatch = useDispatch();
	const { data, error, loading } = useSelector(select);
	const { handleCheckout } = useEvent(Number(data.id));
	const history = useHistory();

	const _navTitle = 'Selecione um assento';
	const seatsMapContainerRef = React.useRef<HTMLDivElement>(null);

	const [libLoaded, setLibLoaded] = useState(false);
	const [mapFromPrice, setMapFromPrice] = useState<number | null>(null);
	const [generalFromPrice, setGeneralFromPrice] = useState<number | null>(null);

	useEffect(() => {
		dispatch(clearEvent());
		dispatch(hideMobileOptions());
		dispatch(hideFixedFooterContent());
	}, []);

	useEffect(() => {
		dispatch(getEvent(url));
	}, [dispatch]);

	const loadMap = () => {
		if (data.seatsMapActived && !customElements.get('seats-map-element')) {
			const remoteUrl = process.env.REACT_APP_SEATS_MAP_MF_URL || 'http://localhost:5001/assets/remoteEntry.js';

			loadRemote(remoteUrl)
				.then(() => {
					setTimeout(() => {
						setLibLoaded(true);
					}, 600)
				})
				.catch(err => alert(`EventSeatsMapPage: Failed to load Seats Map Microfrontend: ${err}`));
		} else if (customElements.get('seats-map-element')) {
			setLibLoaded(true);
		}
	}

	useEffect(() => {
		if (data.url != url) return;
		if (data.hasOutsideMapSectors) {
			if (!isMobile) {
				history.push(`/${data.url}`);
				return;
			}
			loadPurchaseModePrices();
			return;
		}
		if (data.seatsMapActived) {
			if (!isMobile) history.push(`/${data.url}`);
			loadMap();
		}
	}, [data.url, data.hasOutsideMapSectors]);

	useEffect(() => {
		if (data.hasOutsideMapSectors || !libLoaded) return;

		const seatsMapElement =
			seatsMapContainerRef.current?.querySelector('seats-map-element');

		if (!seatsMapElement) return;

		const checkoutEventListener = () => {
			handleCheckout();
		};

		seatsMapElement.addEventListener('checkout-event', checkoutEventListener);

		return () => {
			seatsMapElement.removeEventListener('checkout-event', checkoutEventListener);
		};
	}, [libLoaded, handleCheckout, data.hasOutsideMapSectors]);

	useEffect(() => {
		return () => {
			dispatch(showMobileOptions());
			dispatch(hideFixedFooterContent());
			dispatch(clearEvent());
		}
	}, []);

	const loadPurchaseModePrices = async () => {
		try {
			const [outsideMapResponse, ticketsResponse] = await Promise.all([
				OutsideMapService.getOutsideMap(eventDate),
				TicketService.getAvailableTicketsByEventDate(data.id, eventDate),
			]);

			const priceById = new Map<number, number>(
				ticketsResponse.data
					.filter((ticket: AvailableTicket) => ticket.visible && ticket.quantity > 0)
					.map((ticket: AvailableTicket) => [Number(ticket.id), ticket.price])
			);
			const outsideMapTicketIds = outsideMapResponse.data.sectors
				.filter(sector => sector.remaining > 0)
				.flatMap(sector => sector.ticketIds);

			setMapFromPrice(getMinPrice(outsideMapResponse.data.mapTicketIds, priceById));
			setGeneralFromPrice(getMinPrice(outsideMapTicketIds, priceById));
		} catch (err) {
			console.error('EventSeatsMapPage: Failed to load purchase mode prices:', err);
		}
	}

	const getMinPrice = (ticketIds: number[], priceById: Map<number, number>): number | null => {
		const prices = ticketIds
			.map(id => priceById.get(Number(id)))
			.filter((price): price is number => price !== undefined);

		return prices.length ? Math.min(...prices) : null;
	}

	const getPriceLabel = (price: number | null) => price !== null ? `A partir de ${formatCurrency(price)}` : '';

	const getLoading = () => {
		if (loading && !error) {
			return (<LoadingSpinner />)
		}
	}

	const handleBack = () => {
		if (history.length > 1) {
			history.goBack();
		} else {
			history.push(`/${url}`);
		}
	};

	const goToMap = () => history.push(`${history.location.pathname}/map`);
	const goToGeneral = () => history.push(`${history.location.pathname}/general`);

	return (<>
		{error ? <PageNotFoundMessage /> : (<>
			<EventSeatsContainer>
				<EventSeatsMobileNavBar>
					<NavBarContent>
						<NavIconContainer onClick={handleBack}><ArrowLeft /></NavIconContainer>
						<NavTitleContainer>
							{loading ? <Skeleton height={20} width={150} /> :
								<NavEventTitle>{data.name}</NavEventTitle>}
							<NavTitle>{data.hasOutsideMapSectors ? 'Como você quer comprar?' : _navTitle}</NavTitle>
						</NavTitleContainer>
					</NavBarContent>
					{!loading && !data.hasOutsideMapSectors && (
						<MapCaptionContainer>
							<MapCaption>
								<MapCaptionCircle $color={'#32a6f8'} />
								Selecionado
							</MapCaption>
							<MapCaption>
								<MapCaptionCircle $color={'#ececec'} />
								Indisponível
							</MapCaption>
						</MapCaptionContainer>
					)}
				</EventSeatsMobileNavBar>
				{loading ? getLoading() : (<>
					{data.hasOutsideMapSectors ? (
						<PurchaseModeSection>
							<PurchaseModeOption onClick={goToMap}>
								<PurchaseModeContent>
									<PurchaseModeTitle>Escolher meu lugar no mapa</PurchaseModeTitle>
									<PurchaseModeSubtitle>{getPriceLabel(mapFromPrice)}</PurchaseModeSubtitle>
								</PurchaseModeContent>
								<ChevronRight color={Variables.colors.dark} />
							</PurchaseModeOption>

							<PurchaseModeOption onClick={goToGeneral}>
								<PurchaseModeContent>
									<PurchaseModeTitle>Entrada sem lugar marcado</PurchaseModeTitle>
									<PurchaseModeSubtitle>{getPriceLabel(generalFromPrice)}</PurchaseModeSubtitle>
								</PurchaseModeContent>
								<ChevronRight color={Variables.colors.dark} />
							</PurchaseModeOption>
						</PurchaseModeSection>
					) : (
						<SeatsMapContainer>
							<RenderIf condition={data.seatsMapActived}>
								<SeatsMapContentContainer ref={seatsMapContainerRef}>
									{libLoaded && (<seats-map-element
										tenant-id={process.env.REACT_APP_TENANT_ID}
										event-id={data.id}
										event-dates={JSON.stringify(data.eventDates)}
										event-date-id={Number(eventDate)}
										view-mode="VIEWER"
										stage-width={seatsMapContainerRef?.current?.clientWidth}
										stage-height={800}
										has-outside-map-sectors="false"
										initial-mode="map"
									/>)}
								</SeatsMapContentContainer>
							</RenderIf>
						</SeatsMapContainer>
					)}
				</>)}
			</EventSeatsContainer>
		</>)}
	</>);
};

export default EventSeatsMapPage;
